import type { ApolloError } from '@apollo/client';
import { mapTaxonomiesToDropdownItems } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../../data';
import {
  updateWorkTypes,
  updateWorkTypesFailure,
  updateWorkTypesSuccess,
} from '../actionCreators';

import translations from './.vocab';
import {
  generateUpdateWorkTypesOptimisticResponse,
  updateCacheUpdateWorkTypes,
} from './apolloCache';
import type { WorkType, WorkTypeOption } from './types';

export const useWorkTypes = () => {
  const { loading, error, data } = useQuery(Schema.GetWorkTypesDocument);

  const workTypesOption =
    loading || error ? null : data?.viewer?.workTypes || null;

  const workTypes = workTypesOption?.map((option) => option.id) || [];

  return { workTypes, loading, error };
};

const key = 'next-role-work-types';

export const useUpdateWorkTypes = () => {
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { t } = useTranslations(translations);

  const onMutate = () => {
    dispatch(updateWorkTypes());
  };
  const onCompleted = () => {
    dispatch(updateWorkTypesSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateWorkTypesFailure(error));
  };

  return useToastMutation(Schema.UpdatePreferredWorkTypesDocument, {
    key,
    errorActionRouteParameters: {
      card: 'work-types',
      mode: 'EDIT',
    },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Preferred work types updated'),
    update: updateCacheUpdateWorkTypes,
    optimisticResponse: generateUpdateWorkTypesOptimisticResponse({
      _id: candidateId || '',
    }),
  });
};

export const useWorkTypeOptions = () => {
  const { language } = useAppConfig();

  const { data, loading, error, refetch } = useQuery(
    Schema.GetWorkTypeOptionsDocument,
    {
      variables: {
        languageCode: language,
      },
      bypassErrorPageOnFail: true,
    },
  );

  const workTypeOptionsData = useMemo(
    () => (loading || error ? [] : data?.workTypeOptions || []),
    [data, error, loading],
  );

  const getSelectedWorkTypeLabel = useCallback(
    (worktypeId: number) =>
      workTypeOptionsData.find((item) => item.id === worktypeId)?.description,
    [workTypeOptionsData],
  );

  return {
    getSelectedWorkTypeLabel,
    workTypeOptions: mapTaxonomiesToDropdownItems(
      workTypeOptionsData,
    ) as WorkTypeOption[],
    loading,
    error,
    refetch,
  };
};

export const mockGetWorkTypesRequest = (workTypesOptions: WorkType[]) => {
  const workTypes = workTypesOptions.map((option) => ({
    __typename: 'WorkType',
    id: option,
  }));
  return {
    request: {
      query: Schema.GetWorkTypesDocument,
    },
    result: {
      data: {
        viewer: {
          _id: '1234',
          workTypes,
        },
      },
    },
  };
};

export const mockGetWorkTypeOptionsRequest = (
  workTypeOptions: Schema.Maybe<Schema.WorkTypeTaxonomyOption[]>,
) => ({
  request: {
    query: Schema.GetWorkTypeOptionsDocument,
    variables: { languageCode: 'en' },
  },
  result: {
    data: {
      workTypeOptions,
    },
  },
});
